import {
  AnalysisFeeCalculatorDTO,
  FeeCalculatorValidStateConditions,
} from "@models/platform-analysis/entities/steps/fee";
import { AnalysisVariable } from "@models/platform-analysis/enums/variableName";
import { AnalysisFeeCalculatorErrorMessages } from "@pages/platform-analysis-v2/_id/steps/fee/calculator/constants";
import {
  calculateFeeCalculatorComparisonValue,
  checkValidPortfolioSetupSection,
  compileErrorTemplateMessage,
  extractAllVariablesFromEvaluateStateConditions,
} from "@pages/platform-analysis-v2/_id/steps/fee/calculator/utils";
import { compareNumberValuesByOperator } from "@utils";
import { useMemo } from "react";
import { FieldPath, useWatch } from "react-hook-form";
import { useFeeCalculator } from "./useFeeCalculator";

export const useFeeCalculatorSectionErrorStates = ({
  conditionConfigs,
  isPortfolioSetupSection,
}: {
  conditionConfigs?: FeeCalculatorValidStateConditions[];
  isPortfolioSetupSection?: boolean;
}) => {
  const {
    formMethods: { control, getValues },
    calculatedTotals,
    pathsMap,
    isNoPortfolioLevel,
  } = useFeeCalculator();

  const variables =
    extractAllVariablesFromEvaluateStateConditions(conditionConfigs);
  const variableFieldPaths = useMemo(() => {
    return variables.map(
      (variable) =>
        pathsMap[variable]
          ?.dataValuePath as FieldPath<AnalysisFeeCalculatorDTO>,
    );
  }, [pathsMap, variables]);

  const trackingValues = useWatch({
    control: control,
    name: variableFieldPaths,
  });

  const watchedPortfolioSetups = useWatch({ control, name: "portfolioSetups" });

  const errorStates = useMemo(() => {
    if (!conditionConfigs && !isPortfolioSetupSection) return [];

    const states: string[] = [];

    conditionConfigs?.forEach((condition) => {
      const {
        leftHandComparison,
        rightHandComparison,
        operator,
        messageTemplate,
      } = condition;

      const leftValue = calculateFeeCalculatorComparisonValue({
        config: leftHandComparison,
        totals: calculatedTotals,
        getValues,
        pathsMap,
      });

      const rightValue = calculateFeeCalculatorComparisonValue({
        config: rightHandComparison,
        totals: calculatedTotals,
        getValues,
        pathsMap,
      });

      const conditionMet = compareNumberValuesByOperator(
        operator,
        leftValue,
        rightValue,
      );

      if (conditionMet || !messageTemplate) return;

      const relatedFieldName: Partial<Record<AnalysisVariable, string>> = {};
      messageTemplate.variables?.forEach((variable) => {
        const fieldName = pathsMap[variable]?.databaseName;
        if (fieldName) relatedFieldName[variable] = fieldName;
      });

      const msg = compileErrorTemplateMessage(
        messageTemplate,
        relatedFieldName,
      );

      states.push(msg);
    });

    if (!isPortfolioSetupSection) return states;

    const isSectionValid = isNoPortfolioLevel
      ? true
      : checkValidPortfolioSetupSection(watchedPortfolioSetups || []);

    if (!isSectionValid)
      states.push(AnalysisFeeCalculatorErrorMessages.InvalidPortfolioSetup);

    return states;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    conditionConfigs,
    pathsMap,
    calculatedTotals,
    trackingValues,
    watchedPortfolioSetups,
    isNoPortfolioLevel,
  ]);

  return { errorStates };
};
